import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import GridCard from '../components/GridCard'
import GeneralDocs from '../components/GeneralDocs'
import GeneralInfo from '../components/GeneralInfo'
import SelectionBar from '../components/selection/SelectionBar'
import { config } from '../settings/configs'
import {
  useGetArbitraggiStatsQuery,
  useGetCampionatiQuery,
  useGetCalendariStatsQuery,
  useGetCalendariProvvedimentiStatsQuery,
  useGetDesignazioniStatsQuery,
  useGetDistinteStatsQuery,
  useGetIscrizioniStatsQuery,
  useGetRendicontiStatsQuery,
  useGetRimborsiStatsQuery,
  useGetSpostamentiStatsQuery,
  useGetTesseratiStatsQuery,
} from '../hooks/api-slice'

const DashboardAdmin = () => {
  const { data: campionati } = useGetCampionatiQuery()
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )

  document.title = `${config?.name} - Home`

  const cArgs = [selectedCampionato, { skip: !selectedCampionato }]
  const { data: iscrizioniStats } = useGetIscrizioniStatsQuery(...cArgs)
  const { data: spostamentiStats } = useGetSpostamentiStatsQuery(...cArgs)
  const { data: partiteStats } = useGetCalendariStatsQuery(...cArgs)
  const { data: arbitraggiStats } = useGetArbitraggiStatsQuery(...cArgs)

  const dArgs = [
    { campionato: selectedCampionato },
    { skip: !selectedCampionato },
  ]
  const { data: designazioniStats } = useGetDesignazioniStatsQuery(...dArgs)
  const { data: distinteStats } = useGetDistinteStatsQuery(...dArgs)

  const { data: risultatiMancanti } = useGetCalendariProvvedimentiStatsQuery(
    { selectedProvvedimento: 'risultati-mancanti', selectedCampionato },
    { skip: !selectedCampionato },
  )
  const { data: refertiMancanti } = useGetCalendariProvvedimentiStatsQuery(
    { selectedProvvedimento: 'referti-mancanti', selectedCampionato },
    { skip: !selectedCampionato },
  )

  const stagione = campionati?.find(
    c => c.codice === selectedCampionato,
  )?.stagione
  const sSkip = { skip: !stagione }
  const { data: rendicontiStats } = useGetRendicontiStatsQuery(stagione, sSkip)
  const { data: tesseratiStats } = useGetTesseratiStatsQuery(
    { stagione },
    sSkip,
  )
  const { data: rimborsiStats } = useGetRimborsiStatsQuery({ stagione }, sSkip)

  return (
    <Grid
      container
      paddingX={2}
      marginY={2}
      rowSpacing={3}
      columnSpacing={2}
      alignItems='stretch'
      sx={{ width: '100%' }}
    >
      <GeneralInfo />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar yCampionato />
      </Grid>
      {config?.features?.iscrizioni && (
        <GridCard
          title='Iscrizioni'
          subtitle1={`${iscrizioniStats?.complete || 0} ${
            iscrizioniStats?.complete === 1 ? 'completa' : 'complete'
          }`}
          subtitle2={`${iscrizioniStats?.incomplete || 0} ${
            iscrizioniStats?.incomplete === 1
              ? 'da completare'
              : 'da completare'
          }`}
          linkTo='/iscrizioni'
          isError={iscrizioniStats?.incomplete > 0}
        />
      )}
      {config?.features?.spostamenti && (
        <GridCard
          title='Spostamenti'
          subtitle1={`${spostamentiStats?.accepted || 0} ${
            spostamentiStats?.accepted === 1 ? 'accettato' : 'accettati'
          }`}
          subtitle2={`${spostamentiStats?.waiting || 0} ${
            spostamentiStats?.waiting === 1 ? 'in sospeso' : 'in sospeso'
          }`}
          linkTo='/spostamenti'
          isError={spostamentiStats?.waiting > 0}
        />
      )}
      {config?.features?.designazioni && (
        <GridCard
          title='Designazioni'
          subtitle1={`${designazioniStats?.missing10days || 0} ${
            designazioniStats?.missing10days === 1
              ? 'mancante (10 giorni)'
              : 'mancanti (10 giorni)'
          }`}
          subtitle2={`${designazioniStats?.waiting || 0} ${
            designazioniStats?.waiting === 1 ? 'in sospeso' : 'in sospeso'
          }`}
          linkTo='/designazioni'
          isError={
            designazioniStats?.waiting + designazioniStats?.missing10days > 0
          }
        />
      )}
      {config?.features?.calendari && (
        <GridCard
          title='Calendari'
          subtitle1={`${partiteStats?.all || 0} partite`}
          subtitle2={`${partiteStats?.withResult || 0} ${
            partiteStats?.withResult === 1
              ? 'risultato inserito'
              : 'risultati inseriti'
          }`}
          linkTo='/calendari'
          isError={
            partiteStats?.withResult + partiteStats?.noDateNoResult <
            partiteStats?.all
          }
        />
      )}
      {config?.features?.provvedimenti && (
        <GridCard
          title='Provvedimenti'
          subtitle1={`${risultatiMancanti || 0} ${
            risultatiMancanti === 1
              ? 'risultato mancante'
              : 'risultati mancanti'
          }`}
          subtitle2={`${refertiMancanti || 0} ${
            refertiMancanti === 1 ? 'referto mancante' : 'referti mancanti'
          }`}
          linkTo='/provvedimenti'
          isError={risultatiMancanti > 0 || refertiMancanti > 0}
        />
      )}
      {config?.features?.arbitraggi && (
        <GridCard
          title='Arbitraggi'
          subtitle1={`${arbitraggiStats?.nRequired || 0} ${
            arbitraggiStats?.nRequired === 1 ? 'richiesta' : 'richieste'
          }`}
          linkTo='/arbitraggi'
        />
      )}
      {config?.features?.rimborsi && (
        <GridCard
          title='Rimborsi'
          subtitle1={`${rimborsiStats?.nonPagati || 0} da pagare`}
          subtitle2={`${rimborsiStats?.pagati || 0} ${
            rimborsiStats?.pagati === 1 ? 'pagato' : 'pagati'
          }`}
          linkTo='/rimborsi'
          isError={rimborsiStats?.nonPagati > 0}
        />
      )}
      {config?.features?.rendiconti && (
        <GridCard
          title='Rendiconti'
          subtitle1={`${rendicontiStats?.all || 0} ${
            rendicontiStats?.all === 1 ? 'totale' : 'totali'
          }`}
          subtitle2={`${rendicontiStats?.paid || 0} ${
            rendicontiStats?.paid === 1 ? 'pagato' : 'pagati'
          }`}
          linkTo='/rendiconti'
          isError={rendicontiStats?.all > rendicontiStats?.paid}
        />
      )}
      {config?.features?.tesserati && (
        <GridCard
          title='Tesserati'
          subtitle1={`${tesseratiStats?.players || 0} ${
            tesseratiStats?.players === 1 ? 'atleta' : 'atleti'
          }`}
          subtitle2={`${tesseratiStats?.staff || 0} ${
            tesseratiStats?.staff === 1 ? 'staff' : 'staff'
          }`}
          linkTo='/tesserati'
        />
      )}
      {config?.features?.distinte && (
        <GridCard
          title='Distinte'
          subtitle1={`${distinteStats?.all || 0} ${
            distinteStats?.all === 1 ? 'distinta' : 'distinte'
          }`}
          linkTo='/distinte'
        />
      )}
      <GeneralDocs />
    </Grid>
  )
}

export default DashboardAdmin
